import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChartBarIcon, 
  DocumentTextIcon, 
  CurrencyRupeeIcon,
  ShieldCheckIcon,
  CloudIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  StarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { logSignOut } from '../lib/firebase/analytics';
import SEO from './SEO';
import './LandingPage.css';

interface LandingPageProps {
  onGetStarted?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  const { user, loading, authLoading, signIn, logOut } = useAuth();
  
  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState<string>('');
  
  // Download throttling state
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadCooldown, setDownloadCooldown] = useState(0);

  const handleDownload = () => {
    // Prevent multiple downloads
    if (isDownloading || downloadCooldown > 0) {
      return;
    }
    
    setIsDownloading(true);
    setDownloadCooldown(10); // 10 seconds cooldown
    
    // Firebase Storage download URL
    const downloadUrl = 'https://firebasestorage.googleapis.com/v0/b/invosathi.firebasestorage.app/o/InvoSathi.zip?alt=media';
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'InvoSathi.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Reset download state after a short delay
    setTimeout(() => {
      setIsDownloading(false);
    }, 10000);
    
    // Start cooldown countdown
    const cooldownInterval = setInterval(() => {
      setDownloadCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Call the original onGetStarted if provided
    if (onGetStarted) {
      onGetStarted();
    }
  };

  const openLightbox = (imageSrc: string, alt: string) => {
    setLightboxImage(imageSrc);
    setLightboxAlt(alt);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxAlt('');
    document.body.style.overflow = 'unset'; // Restore scrolling
  };

  // Handle ESC key press to close lightbox
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && lightboxImage) {
        closeLightbox();
      }
    };

    if (lightboxImage) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [lightboxImage]);

  const handleSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await signIn();
    } catch (error) {
      console.error('Sign-in error:', error);
      // You can add a toast or alert here to show the error to the user
    }
  };

  // If user is authenticated, show user profile section
  if (user) {
    return (
      <div className="landing-page">
        <SEO 
          title="Dashboard - InvoSathi"
          description="Access your InvoSathi dashboard to manage invoices, track revenue, and handle GST compliance efficiently."
        />
        {/* Header with User Profile */}
        <header className="header">
          <nav className="nav">
            <div className="nav-content">
              <div className="logo">
                <h1>InvoSathi</h1>
                <span>Your Invoice Partner</span>
              </div>
              <div className="auth-container">
                <div className="user-profile">
                  <img 
                    src={user.photoURL || '/default-avatar.webp'} 
                    alt="User Avatar"
                    className="user-avatar"
                  />
                  <div className="user-info">
                    <h3>Welcome, {user.displayName}!</h3>
                    <p>{user.email}</p>
                  </div>
                  <button 
                    onClick={() => {
                      logOut();
                      logSignOut();
                    }}
                    className="auth-button logout-button"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </nav>
        </header>
        {/* Rest of the landing page content for authenticated users */}
        <section className="hero">
          <div className="hero-content">
            <motion.div 
              className="hero-text"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>Welcome back to InvoSathi!</h1>
              <p>
                You're signed in and ready to manage your invoices. Access your dashboard to get started.
              </p>
              <div className="hero-buttons">
                <button className="btn-base btn-primary-large" onClick={onGetStarted}>Go to Dashboard</button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  // If loading, show loading state
  if (loading) {
    return (
      <div className="landing-page">
        <div className="auth-container">
          <div className="auth-loading">Loading...</div>
        </div>
      </div>
    );
  }

  const features = [
    {
      icon: <DocumentTextIcon className="w-8 h-8" />,
      title: "Smart Invoice Management",
      description: "Create, manage, and track invoices with automated GST calculations and compliance."
    },
    {
      icon: <ChartBarIcon className="w-8 h-8" />,
      title: "Real-time Analytics",
      description: "Get insights into your business performance with comprehensive reporting and analytics."
    },
    {
      icon: <CurrencyRupeeIcon className="w-8 h-8" />,
      title: "GST Compliance",
      description: "Stay compliant with automated GST calculations, filing assistance, and tax management."
    },
    {
      icon: <ShieldCheckIcon className="w-8 h-8" />,
      title: "Secure & Private",
      description: "Your data stays on your computer with encrypted local storage and secure offline operation."
    },
    {
      icon: <CloudIcon className="w-8 h-8" />,
      title: "Cross-Platform",
      description: "Works seamlessly on Windows, Mac, and Linux - install once, use everywhere."
    },
    {
      icon: <DevicePhoneMobileIcon className="w-8 h-8" />,
      title: "Offline Ready",
      description: "No internet required! Work offline with full functionality and sync when you need to."
    }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      business: "Kumar Enterprises",
      rating: 5,
      text: "InvoSathi desktop app has revolutionized our billing process. Works perfectly offline and GST compliance is now effortless!"
    },
    {
      name: "Priya Sharma",
      business: "Sharma Trading Co.",
      rating: 5,
      text: "The best desktop invoice management solution we've used. No internet required and highly recommended for small businesses."
    },
    {
      name: "Amit Patel",
      business: "Patel Industries",
      rating: 5,
      text: "Excellent desktop software with great support. Our accounting is now streamlined and we love the offline capability."
    }
  ];

  const pricingPlans = [
    {
      name: "Free Forever",
      price: "₹0",
      period: "Lifetime",
      features: [
        "✨ Full Desktop Application",
        "🎯 Complete GST Compliance Suite",
        "💫 Unlimited Invoices & Customers",
        "🖥️ Works on Windows, Mac & Linux",
        "⚡ No Internet Required",
        "🚀 Regular Free Updates",
        "📧 Premium Email Support"
      ],
      isPopular: true
    }
  ];

  return (
    <div className="landing-page">
      <SEO 
        title="InvoSathi - Professional Desktop Invoice Management for Indian Businesses"
        description="InvoSathi is a powerful desktop application that helps Indian businesses manage invoices, comply with GST regulations, and track revenue - all running securely on your computer without internet dependency."
      />
      {/* Header */}
      <header className="header">
        <nav className="nav">
          <div className="nav-content">
            <div className="logo">
              <h1>InvoSathi</h1>
              <span>Your Invoice Partner</span>
            </div>
            <div className="nav-links">
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#testimonials">Reviews</a>
              <a href="#contact">Contact</a>
              <button className="btn-base btn-secondary" onClick={handleSignIn} disabled={authLoading}>
                {authLoading ? 'Signing In...' : 'Sign In'}
              </button>
              <button 
                className="btn-base btn-primary" 
                onClick={handleDownload} 
                disabled={isDownloading || downloadCooldown > 0}
              >
                {isDownloading ? 'Downloading...' : 
                 downloadCooldown > 0 ? `Wait ${downloadCooldown}s` : 
                 'Download Trial'}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <motion.div 
            className="hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Professional Desktop Invoice Management for Your Business</h1>
            <p>
              InvoSathi is a powerful desktop application that helps Indian businesses manage invoices, 
              comply with GST regulations, and track revenue - all running securely on your computer without internet dependency.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-base btn-primary-large" 
                onClick={handleDownload}
                disabled={isDownloading || downloadCooldown > 0}
              >
                {isDownloading ? 'Downloading...' : 
                 downloadCooldown > 0 ? `Wait ${downloadCooldown}s` : 
                 'Download Free Trial'}
              </button>
              <button className="btn-base btn-secondary-large">Watch Demo</button>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <h3>50,000+</h3>
                <p>Downloads</p>
              </div>
              <div className="stat">
                <h3>5M+</h3>
                <p>Invoices Generated</p>
              </div>
              <div className="stat">
                <h3>100%</h3>
                <p>Offline Ready</p>
              </div>
            </div>
          </motion.div>
          <motion.div 
            className="hero-image"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="hero-screenshot">
              <img 
                src="/images/127.0.0.1_5123_.webp" 
                alt="InvoSathi Desktop Application Dashboard"
                className="hero-main-image clickable-image"
                onClick={() => openLightbox("/images/127.0.0.1_5123_.webp", "InvoSathi Desktop Application Dashboard")}
              />
              <div className="screenshot-badge">
                <span>✨ Live Dashboard</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Everything You Need in One Desktop Application</h2>
            <p>Powerful features designed specifically for Indian businesses - works completely offline</p>
          </motion.div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Software Showcase Section */}
      <section className="software-showcase">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>See InvoSathi in Action</h2>
            <p>Explore the powerful features and intuitive interface of our desktop application</p>
          </motion.div>
          <div className="showcase-grid">
            <motion.div
              className="showcase-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="showcase-image">
                <img 
                  src="/images/127.0.0.1_5123_Sales_History.webp" 
                  alt="Sales History & Invoice Management" 
                  className="clickable-image"
                  onClick={() => openLightbox("/images/127.0.0.1_5123_Sales_History.webp", "Sales History & Invoice Management")}
                />
              </div>
              <div className="showcase-content">
                <h3>Sales History & Reports</h3>
                <p>Track all your sales, generate detailed reports, and monitor business performance with comprehensive analytics.</p>
              </div>
            </motion.div>
            
            <motion.div
              className="showcase-item showcase-reverse"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="showcase-image">
                <img 
                  src="/images/127.0.0.1_5123_Customer.webp" 
                  alt="Customer Management System" 
                  className="clickable-image"
                  onClick={() => openLightbox("/images/127.0.0.1_5123_Customer.webp", "Customer Management System")}
                />
              </div>
              <div className="showcase-content">
                <h3>Customer Management</h3>
                <p>Organize customer information, track payment history, and manage relationships with an intuitive customer database.</p>
              </div>
            </motion.div>
            
            <motion.div
              className="showcase-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="showcase-image">
                <img 
                  src="/images/127.0.0.1_5123_Product.webp" 
                  alt="Product & Inventory Management" 
                  className="clickable-image"
                  onClick={() => openLightbox("/images/127.0.0.1_5123_Product.webp", "Product & Inventory Management")}
                />
              </div>
              <div className="showcase-content">
                <h3>Product & Inventory</h3>
                <p>Manage your product catalog, track inventory levels, and streamline stock management with automated alerts.</p>
              </div>
            </motion.div>

            <motion.div
              className="showcase-item showcase-reverse"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="showcase-image">
                <img 
                  src="/images/127.0.0.1_5123_purchase.webp" 
                  alt="Purchase Order Management" 
                  className="clickable-image"
                  onClick={() => openLightbox("/images/127.0.0.1_5123_purchase.webp", "Purchase Order Management")}
                />
              </div>
              <div className="showcase-content">
                <h3>Purchase Management</h3>
                <p>Create purchase orders, manage supplier relationships, and track procurement with complete purchase lifecycle management.</p>
              </div>
            </motion.div>
          </div>
          
          {/* Additional Features Gallery */}
          <motion.div 
            className="features-gallery"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <h3>More Powerful Features</h3>
            <div className="gallery-grid">
              <div className="gallery-item" onClick={() => openLightbox("/images/127.0.0.1_5123_Admin.webp", "Admin Panel")}>
                <img src="/images/127.0.0.1_5123_Admin.webp" alt="Admin Panel" className="clickable-image" />
                <div className="gallery-overlay">
                  <h4>Admin Panel</h4>
                  <p>Complete administrative control</p>
                </div>
              </div>
              <div className="gallery-item" onClick={() => openLightbox("/images/127.0.0.1_5123_Supplyer.webp", "Supplier Management")}>
                <img src="/images/127.0.0.1_5123_Supplyer.webp" alt="Supplier Management" className="clickable-image" />
                <div className="gallery-overlay">
                  <h4>Supplier Management</h4>
                  <p>Manage vendor relationships</p>
                </div>
              </div>
              <div className="gallery-item" onClick={() => openLightbox("/images/127.0.0.1_5123_Backup.webp", "Data Backup")}>
                <img src="/images/127.0.0.1_5123_Backup.webp" alt="Data Backup" className="clickable-image" />
                <div className="gallery-overlay">
                  <h4>Data Backup</h4>
                  <p>Secure data protection</p>
                </div>
              </div>
              <div className="gallery-item" onClick={() => openLightbox("/images/127.0.0.1_5123_AuditLog.webp", "Audit Trail")}>
                <img src="/images/127.0.0.1_5123_AuditLog.webp" alt="Audit Trail" className="clickable-image" />
                <div className="gallery-overlay">
                  <h4>Audit Trail</h4>
                  <p>Complete activity tracking</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Completely Free for Everyone! 🎉</h2>
            <p className="pricing-subtitle">We believe in empowering Indian businesses with free, high-quality software</p>
          </motion.div>
          <div className="pricing-grid special-offer">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                className={`pricing-card ${plan.isPopular ? '  popular' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                {plan.isPopular && <div className="popular-badge">Most Popular</div>}
                <h3>{plan.name}</h3>
                <div className="pricing-amount">
                  <span className="price">{plan.price}</span>
                  <span className="period">{plan.period}</span>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <CheckCircleIcon className="w-5 h-5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button 
                  className="btn-base btn-pricing btn-primary download-free"
                  onClick={handleDownload}
                  disabled={isDownloading || downloadCooldown > 0}
                >
                  {isDownloading ? '⏳ Downloading...' : 
                   downloadCooldown > 0 ? `⌛ Wait ${downloadCooldown}s` : 
                   '🎁 Download Free Software'}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What Our Customers Say</h2>
            <p>Join thousands of satisfied businesses across India</p>
          </motion.div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 filled" />
                  ))}
                </div>
                <p>"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h4>{testimonial.name}</h4>
                    <span>{testimonial.business}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Transform Your Business?</h2>
            <p>Download InvoSathi today and start managing your invoices like a pro - no internet required!</p>
            <div className="cta-buttons">
              <button 
                className="btn-base btn-primary-large" 
                onClick={handleDownload}
                disabled={isDownloading || downloadCooldown > 0}
              >
                {isDownloading ? 'Downloading...' : 
                 downloadCooldown > 0 ? `Wait ${downloadCooldown}s` : 
                 'Download Free Trial'}
              </button>
              <a 
                href="https://wa.me/918237553896?text=Hi%2C%20I%27m%20interested%20in%20InvoSathi%20desktop%20software.%20Can%20you%20help%20me%20get%20started%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-base btn-secondary-large whatsapp-cta"
              >
                💬 Chat on WhatsApp
              </a>
            </div>
            <p className="cta-note">No credit card required • 30-day free trial • Works on Windows, Mac & Linux</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <h3>InvoSathi</h3>
                <p>Professional desktop software for invoice management and GST compliance in India.</p>
              </div>
              <div className="footer-social">
                <a href="#" aria-label="Facebook">📘</a>
                <a href="#" aria-label="Twitter">🐦</a>
                <a href="#" aria-label="LinkedIn">💼</a>
                <a href="#" aria-label="Instagram">📷</a>
              </div>
            </div>
            <div className="footer-section">
              <h4>Product</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#">API</a></li>
                <li><a href="#">Integrations</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Support</h4>
              <ul>
                <li><a href="#">Help Center</a></li>
                <li><a href="#">Documentation</a></li>
                <li><a href="#">Contact Us</a></li>
                <li><a href="#">Training</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li><a href="#">About Us</a></li>
                <li><a href="#">Careers</a></li>
                <li><a href="#">Privacy Policy</a></li>
                <li><a href="#">Terms of Service</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <ul>
                {/* <li>📧 support@invosathi.com</li> */}
                <li>📞 +91 8237553896</li>
                <li>
                  <a 
                    href="https://wa.me/918237553896?text=Hi%2C%20I%27m%20interested%20in%20InvoSathi%20desktop%20software.%20Can%20you%20help%20me%20get%20started%3F" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="whatsapp-link"
                  >
                    💬 WhatsApp: +91 8237553896
                  </a>
                </li>
                <li>📍 Pune, Maharashtra, India</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 InvoSathi. All rights reserved.</p>
            <p>Made with ❤️ in India</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/918237553896?text=Hi%2C%20I%27m%20interested%20in%20InvoSathi%20desktop%20software.%20Can%20you%20help%20me%20get%20started%3F"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Contact us on WhatsApp"
      >
        <div className="whatsapp-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="whatsapp-svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.951 3.488"/>
          </svg>
        </div>
        <span className="whatsapp-text">Chat with us</span>
      </a>

      {/* Image Lightbox Modal */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            className="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <motion.div
              className="lightbox-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="lightbox-close"
                onClick={closeLightbox}
                aria-label="Close image"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              <img
                src={lightboxImage}
                alt={lightboxAlt}
                className="lightbox-image"
              />
              <div className="lightbox-caption">
                {lightboxAlt}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
